/**
 * Pure helpers that turn a search row into the card's meta line and featured
 * service(s). Every redesign field is optional (the backend may not have shipped
 * it), so each helper degrades to the pre-redesign fields.
 */
import type { SearchRow } from '../api/contracts';

export interface FeaturedService {
  /** Absent for legacy `topServices` rows (no id on the wire). */
  id: string | null;
  name: string;
  durationMinutes: number;
  price: number;
  priceFrom: boolean;
}

/** City from a location address: JSON `{city}` (tenant locations) or "Street 1, 8400 Oostende". */
export function cityFromAddress(raw: string | null | undefined): string {
  if (!raw) return '';
  const s = raw.trim();
  if (s.startsWith('{')) {
    try {
      return ((JSON.parse(s) as { city?: string }).city ?? '').trim();
    } catch {
      return '';
    }
  }
  const last = s.split(',').pop()?.trim() ?? '';
  // Drop a leading postcode: "8400 Oostende", "4524 Sluis", "1000-001 Lisboa".
  return last.replace(/^[0-9][0-9A-Z -]*\s+(?=\p{L})/u, '').trim();
}

/**
 * The row's display city: the top-level `city` (always sent for mobile-only
 * rows, which have `location: null`), else parsed from the location address.
 */
export function rowCity(row: Pick<SearchRow, 'city' | 'location'>): string {
  return row.city?.trim() || cityFromAddress(row.location?.address);
}

/** Up to `max` featured services: the search's matching services, else the salon's top services. */
export function featuredServices(row: SearchRow, max = 1): FeaturedService[] {
  if (row.matchingServices && row.matchingServices.length) {
    return row.matchingServices.slice(0, max).map((s) => ({ ...s, priceFrom: Boolean(s.priceFrom) }));
  }
  return (row.topServices ?? []).slice(0, max).map((s) => ({
    id: null,
    name: s.name,
    durationMinutes: s.durationMinutes,
    price: s.price,
    priceFrom: false,
  }));
}

/** Price for the map pin: the contract's cheapest matching price, else the lowest listed price. */
export function pinPrice(row: SearchRow): number | null {
  if (row.cheapestMatchingPrice != null) return row.cheapestMatchingPrice;
  const prices = [
    ...(row.matchingServices ?? []).map((s) => s.price),
    ...(row.topServices ?? []).map((s) => s.price),
  ].filter((p) => Number.isFinite(p));
  return prices.length ? Math.min(...prices) : null;
}

/**
 * The ONE verified rule for cards and the profile (M3 QA item 8): every listed
 * salon is APPROVED by our team, so only an explicit `verified: false` hides
 * the tick. Older backends omit the field → verified.
 */
export function isVerified(x: { verified?: boolean | null }): boolean {
  return x.verified !== false;
}

/** Rating to display, or null → "New on Salunas" (D-04: never a fake score). */
export function displayRating(row: Pick<SearchRow, 'rating' | 'reviewCount'>): number | null {
  if (row.rating == null || !Number.isFinite(row.rating)) return null;
  if (row.reviewCount != null && row.reviewCount <= 0) return null;
  return row.rating;
}

/**
 * "2.1 km" under 10 km, "12 km" above; locale decimal separator. A whole
 * number stays whole ("4 km", not "4.0 km"): mobile-only rows arrive with
 * `distanceKm` already rounded to the km.
 */
export function formatKm(km: number, locale: string): string {
  const digits = km < 10 && !Number.isInteger(km) ? 1 : 0;
  try {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(km);
  } catch {
    return km.toFixed(digits);
  }
}

export type ReachKind =
  | { kind: 'travels'; km: number }
  | { kind: 'area'; area: string }
  | { kind: 'travelsToYou' }
  | { kind: 'distance'; km: number }
  | { kind: 'none' };

/**
 * Distance for fixed salons, travel radius for mobile ones (design-spec §4.01
 * card data). A mobile salon without `travelRadiusKm` falls back to the legacy
 * free-text `serviceArea` ("Oostende en de kust"), M3 QA item 11.
 */
export function reach(row: SearchRow): ReachKind {
  if (row.isMobile) {
    if (row.travelRadiusKm != null && row.travelRadiusKm > 0) return { kind: 'travels', km: row.travelRadiusKm };
    const area = row.serviceArea?.trim();
    if (area) return { kind: 'area', area };
    if (row.distanceKm == null) return { kind: 'travelsToYou' };
  }
  if (row.distanceKm != null) return { kind: 'distance', km: row.distanceKm };
  return row.isMobile ? { kind: 'travelsToYou' } : { kind: 'none' };
}
